import { useSuggestedQueries, SuggestedQueryItem } from '../../api/cache/appConfigQueryOptions';
import { useStudioAvailability, useStudioVoiceBranding, useStudioVoiceSkills } from '@brandclub/bc-widgets';
import { memo, useCallback, useMemo } from 'react';
import { ConciergeWelcomeFullPage } from './ConciergeWelcomeFullPage';
import { ConciergeWelcomeEmbedded } from './ConciergeWelcomeEmbedded';
import { usePageContext, PageContext } from '../hooks/usePageContext';
import { useConciergeContext } from '../ConciergeContextProvider';

interface ConciergeWelcomeProps {
  trendingSearches?: Array<{ text: string }>;
  onSearch: (searchTerm: string) => void;
  brandId?: number;
  isEmbedded?: boolean;
  apiBaseUrl?: string;
  userFirstName?: string;
  pageContext?: PageContext;
  brandLogoUrl?: string;
  brandName?: string;
  conciergeName?: string;
  // Input state for rendering the search input as part of centered home content
  searchInputValue?: string;
  setSearchInputValue?: (value: string) => void;
  handleSearch?: (searchTerm: string, files?: Array<{ type: 'file'; mediaType: string; url: string }>) => void;
  isActivelyStreaming?: boolean;
  // Mobile: when true, input and starters are excluded from center content
  isMobile?: boolean;
}

const ConciergeWelcomeComponent = ({
  trendingSearches,
  onSearch,
  brandId,
  isEmbedded = false,
  apiBaseUrl,
  userFirstName,
  pageContext: providedPageContext,
  brandLogoUrl,
  brandName,
  conciergeName,
  searchInputValue,
  setSearchInputValue,
  handleSearch,
  isActivelyStreaming,
  isMobile,
}: ConciergeWelcomeProps) => {
  const { data, isLoading: isLoadingSuggestedQueries } = useSuggestedQueries(brandId, apiBaseUrl);
  const studioAvailability = useStudioAvailability();
  const voiceSkills = useStudioVoiceSkills();
  const voiceBranding = useStudioVoiceBranding();
  // Plan 7a / D-63 — voice is now a full-screen overlay mounted by
  // `ConciergeContainer` from context state. The opener allocates a UUID
  // synchronously on the click gesture (no awaits) so iOS Safari
  // preserves the `getUserMedia` user-activation grant.
  const { openVoiceCall } = useConciergeContext();
  const handleVoiceCallClick = useCallback(() => {
    openVoiceCall();
  }, [openVoiceCall]);

  // Memoize suggestedQueries to keep the full objects (with imageUrl)
  const suggestedQueries: SuggestedQueryItem[] = useMemo(
    () => data?.suggestedQuestions || trendingSearches?.map((search) => ({ query: search.text, imageUrl: '' })) || [],
    [data?.suggestedQuestions, trendingSearches],
  );

  // Studio overrides: searchPrompts toggle hides all starters, defaultSearchPrompts override texts
  const displayQueries = useMemo(() => {
    if (studioAvailability?.searchPrompts === false) return [];
    const studioPrompts = studioAvailability?.defaultSearchPrompts?.filter((t) => t.trim());
    if (studioPrompts?.length) return studioPrompts;
    return suggestedQueries.map((item) => item.query);
  }, [suggestedQueries, studioAvailability]);

  // Get page context (use provided or fallback to hook)
  const hookPageContext = usePageContext();
  const pageContext = providedPageContext ?? hookPageContext;

  const commonProps = {
    displayQueries,
    isLoadingSuggestedQueries,
    onSearch,
    pageContext,
    apiBaseUrl,
    brandLogoUrl,
    brandName,
    userName: userFirstName,
    conciergeName,
    searchInputValue,
    setSearchInputValue,
    handleSearch,
    isActivelyStreaming,
    isMobile,
  };

  if (isEmbedded) {
    return <ConciergeWelcomeEmbedded {...commonProps} />;
  }

  // Voice mode (Plan 1) — only the full-page surface gets the CTA. The
  // embedded surface is reused inside the floating widget and bc-widgets
  // contexts where there's no router available; voice mode for those
  // surfaces is deferred to Plan 2 / Plan 4.
  return (
    <ConciergeWelcomeFullPage
      {...commonProps}
      voiceCallEnabled={voiceSkills.enabled}
      onVoiceCallClick={handleVoiceCallClick}
      voiceAssistantName={voiceBranding.persona.assistantName}
    />
  );
};

// Memoize to prevent re-renders when parent re-renders due to input changes
const ConciergeWelcome = memo(ConciergeWelcomeComponent);

export default ConciergeWelcome;
